import {
  ShipmentOrderLegRunItemEntity,
  SplitTargetPayload,
} from 'src/entities/ShipmentOrderLegRun.entity';

// ── 換算係数 ─────────────────────────────────────────────────────────────────
export const WEIGHT_G_PER_KG = 1000;
export const VOLUME_MM3_PER_M3 = 1_000_000_000;

/** g → kg（表示用）。null は null を返す。 */
export function gToKg(g: number | null): number | null {
  return g === null ? null : g / WEIGHT_G_PER_KG;
}
/** kg → g（送信用、整数に丸め）。 */
export function kgToG(kg: number): number {
  return Math.round(kg * WEIGHT_G_PER_KG);
}
/** mm³ → m³（表示用）。null は null。 */
export function mm3ToM3(mm3: number | null): number | null {
  return mm3 === null ? null : mm3 / VOLUME_MM3_PER_M3;
}
/** m³ → mm³（送信用、整数に丸め）。 */
export function m3ToMm3(m3: number): number {
  return Math.round(m3 * VOLUME_MM3_PER_M3);
}

export type SplitAlloc = { quantity: number; weightG: number; volumeMm3: number | null };

export type SplitRow = {
  shipmentOrderItemId: number;
  name: string;
  origQuantity: number;
  origWeightG: number;
  origVolumeMm3: number | null;
  alloc: SplitAlloc[];
};

/**
 * total を n 個に配分する。
 * - 数量1: 各分割先を1で固定（合計 n、元を超過してよい）
 * - 数量2以上: 正確配分（バンプしない。total < n のとき 0 になる先が出るのは許容）
 */
export function allocateQuantity(total: number, n: number): number[] {
  if (n <= 0) return [];
  // 数量1: 各分割先を1で固定（合計 n、元を超過してよい）
  if (total === 1) return Array<number>(n).fill(1);
  // 数量2以上: 正確配分（バンプしない）
  const base = Math.floor(total / n);
  const rem = total - base * n;
  return Array.from({ length: n }, (_, i) => base + (i >= n - rem ? 1 : 0));
}

/** 体積配分の丸め粒度 (mm³)。= 表示の 0.001 m³。重量(g)は表示 0.001kg=1g なので step=1。 */
export const VOLUME_ALLOC_STEP_MM3 = VOLUME_MM3_PER_M3 / 1000;

/**
 * total（整数）を qtyAlloc の比率で配分し、合計が必ず total に一致する整数配列を返す。
 * 先頭 n-1 要素は step の倍数に丸め、末尾要素で端数を吸収する。
 * step は「表示粒度」。重量は g(=表示0.001kg) なので step=1、体積は mm³ で表示0.001m³=1,000,000mm³。
 * これにより、表示(小数3桁)に丸めても各分割先の合計が元の値に一致して見える（末尾が端数を持つ）。
 */
export function allocateProportional(total: number, qtyAlloc: number[], step = 1): number[] {
  const n = qtyAlloc.length;
  if (n === 0) return [];
  if (total === 0) return Array<number>(n).fill(0);

  const sumQ = qtyAlloc.reduce((s, v) => s + v, 0);
  if (sumQ === 0) return Array<number>(n).fill(0);

  const result: number[] = [];
  let allocated = 0;
  for (let i = 0; i < n - 1; i += 1) {
    const val = Math.floor((total * qtyAlloc[i]) / sumQ / step) * step;
    result.push(val);
    allocated += val;
  }
  result.push(total - allocated);
  return result;
}

function buildRowAlloc(row: Pick<SplitRow, 'origQuantity' | 'origWeightG' | 'origVolumeMm3'>, n: number): SplitAlloc[] {
  const qty = allocateQuantity(row.origQuantity, n);
  const weights = allocateProportional(row.origWeightG, qty);
  const volumes = row.origVolumeMm3 === null
    ? null
    : allocateProportional(row.origVolumeMm3, qty, VOLUME_ALLOC_STEP_MM3);
  return Array.from({ length: n }, (_, i) => ({
    quantity: qty[i],
    weightG: weights[i],
    volumeMm3: volumes === null ? null : volumes[i],
  }));
}

export function buildInitialRows(items: ShipmentOrderLegRunItemEntity[], splitCount: number): SplitRow[] {
  return items.map((item) => {
    const base = {
      origQuantity: item.quantity ?? 0,
      origWeightG: item.weight_g ?? 0,
      origVolumeMm3: item.volume_mm3 === null || item.volume_mm3 === undefined ? null : item.volume_mm3,
    };
    return {
      shipmentOrderItemId: item.shipment_order_item_id,
      name: item.name,
      ...base,
      alloc: buildRowAlloc(base, splitCount),
    };
  });
}

/** splitCount 変更時に各行を比例配分で作り直す */
export function reshapeRows(rows: SplitRow[], splitCount: number): SplitRow[] {
  return rows.map((row) => ({ ...row, alloc: buildRowAlloc(row, splitCount) }));
}

export type Remainder = { quantity: number; weightG: number; volumeMm3: number };

export function computeRemainders(rows: SplitRow[]): Remainder[] {
  return rows.map((row) => ({
    quantity: row.origQuantity - row.alloc.reduce((s, a) => s + (a.quantity || 0), 0),
    weightG: row.origWeightG - row.alloc.reduce((s, a) => s + (a.weightG || 0), 0),
    // volume が null の行は origVolumeMm3 を 0 扱いするため残量は常に 0（保全チェック対象外）
    volumeMm3: (row.origVolumeMm3 ?? 0) - row.alloc.reduce((s, a) => s + (a.volumeMm3 ?? 0), 0),
  }));
}

function targetIsEmpty(rows: SplitRow[], target: number): boolean {
  return rows.every((row) => {
    const a = row.alloc[target];
    if (!a) return true; // alloc がまだ reshape されていない（分割数変更直後）
    return (a.quantity || 0) <= 0 && (a.weightG || 0) <= 0 && (a.volumeMm3 ?? 0) <= 0;
  });
}

function hasNegative(rows: SplitRow[]): boolean {
  return rows.some((row) => row.alloc.some(
    (a) => (a.quantity || 0) < 0 || (a.weightG || 0) < 0 || (a.volumeMm3 ?? 0) < 0,
  ));
}

/**
 * 確定可能条件（すべて満たす）:
 * - 負の値が無い（quantity/weightG/volumeMm3 すべて ≥ 0）
 * - 全行で重量残量 === 0 かつ体積残量 === 0（Σ=元 完全保存）
 * - 各行で数量: origQuantity === 1 のみ超過を許容（各分割先1固定・UI入力 disabled）
 *              それ以外（0 や 2以上）は数量残量 === 0（Σ=元 完全一致）
 * - すべての分割先が非空
 */
export function isConfirmable(rows: SplitRow[], splitCount: number): boolean {
  if (hasNegative(rows)) return false;

  const remainders = computeRemainders(rows);
  const weightAndVolumeConserved = remainders.every(
    (r) => r.weightG === 0 && r.volumeMm3 === 0,
  );
  if (!weightAndVolumeConserved) return false;

  // 数量チェック: origQuantity === 1 の行のみ超過を許容（各分割先1固定）。それ以外（0 や 2以上）は完全一致
  const quantityConserved = rows.every((row, i) => {
    if (row.origQuantity === 1) return true;
    return remainders[i].quantity === 0;
  });
  if (!quantityConserved) return false;

  for (let t = 0; t < splitCount; t += 1) {
    if (targetIsEmpty(rows, t)) return false;
  }
  return true;
}

export function toSplitPayload(rows: SplitRow[], splitCount: number): SplitTargetPayload[] {
  return Array.from({ length: splitCount }, (_, t) => ({
    items: rows.map((row) => ({
      shipment_order_item_id: row.shipmentOrderItemId,
      quantity: row.alloc[t].quantity || 0,
      weight_g: row.alloc[t].weightG || 0,
      volume_mm3: row.alloc[t].volumeMm3 === null ? null : row.alloc[t].volumeMm3 || 0,
    })),
  }));
}
